import { useCallback, useMemo } from 'react'

import { TRoomContractSchedule } from '@/entities/room'

import {
  buildDateMap,
  buildProcessedRanges,
  buildSortedRanges,
  buildWeekActiveRangesMap,
  buildWeeklyLaneMapping,
  buildWeekMaxLanesMap,
  ContractBarInfo,
  getContractBarsForDate,
  getRanges,
  getWeekMaxLanes as getWeekMaxLanesForDate,
} from './contract-bars'

type Props = {
  contracts: TRoomContractSchedule[]
}

export const useContractBars = ({ contracts }: Props) => {
  const { dateMap, weekMaxLanesMap } = useMemo(() => {
    if (contracts.length === 0) {
      return {
        dateMap: new Map(),
        weekMaxLanesMap: new Map(),
      }
    }

    /**
     * 레인 배치 알고리즘
     * - 입주일을 기준으로 빠른 계약부터 위 줄에 바가 쌓인다.
     * - 주(week)가 바뀔 때 전 계약이 끝나 빈 공간이 생기면 남은 레인들은 위로 당긴다.
     */

    // 1. 유효한 계약 범위 생성
    const ranges = getRanges(contracts)
    // 2. 계약의 startDate 기준 정렬 (같으면 endDate 기준)
    const sortedRanges = buildSortedRanges(ranges)
    // 3. 계약별 laneIndex, colorIndex 할당
    const processedRanges = buildProcessedRanges(sortedRanges)
    // 4. 주별로 활성화된 계약들의 laneIndex 맵 생성
    const weekActiveRangesMap = buildWeekActiveRangesMap(processedRanges)
    // 5. 주별 레인 인덱스 매핑 생성 (활성화된 계약들만 0부터 연속적으로 매핑)
    const weeklyLaneMapping = buildWeeklyLaneMapping(weekActiveRangesMap)
    // 6. 날짜별 정보를 담은 맵 생성
    const dateMap = buildDateMap(processedRanges, weeklyLaneMapping)
    // 7. 주별 최대 레인 수 맵 생성
    const weekMaxLanesMap = buildWeekMaxLanesMap(dateMap)

    return { dateMap, weekMaxLanesMap }
  }, [contracts])

  const getContractBars = useCallback(
    (date: Date): ContractBarInfo[] => {
      return getContractBarsForDate(dateMap, date)
    },
    [dateMap],
  )

  const getWeekMaxLanes = useCallback(
    (date: Date): number => {
      return getWeekMaxLanesForDate(weekMaxLanesMap, date)
    },
    [weekMaxLanesMap],
  )

  return {
    getContractBars,
    getWeekMaxLanes,
  }
}
